import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PlusIcon, MegaphoneIcon, EnvelopeIcon, CursorArrowRaysIcon, ArrowTrendingUpIcon } from '@heroicons/react/24/outline';
import type { Campaign } from '../lib/types';

export default function Dashboard() {
  const { user } = useAuth();

  const { data: campaigns } = useQuery({
    queryKey: ['campaigns', 'recent'],
    queryFn: () => api.get<{ data: Campaign[] }>('/campaigns?limit=5'),
  });

  const stats = [
    {
      name: 'Total Campaigns',
      value: campaigns?.meta?.total || 0,
      icon: MegaphoneIcon,
      color: 'text-blue-400',
      bgColor: 'bg-blue-400/10',
    },
    {
      name: 'Emails Sent',
      value: campaigns?.data?.reduce((sum, c) => sum + c.sent_count, 0) || 0,
      icon: EnvelopeIcon,
      color: 'text-green-400',
      bgColor: 'bg-green-400/10',
    },
    {
      name: 'Open Rate',
      value: campaigns?.data && campaigns.data.reduce((sum, c) => sum + c.sent_count, 0) > 0
        ? Math.round(
            (campaigns.data.reduce((sum, c) => sum + c.open_count, 0) /
              campaigns.data.reduce((sum, c) => sum + c.sent_count, 0)) *
              100
          ) + '%'
        : '0%',
      icon: CursorArrowRaysIcon,
      color: 'text-yellow-400',
      bgColor: 'bg-yellow-400/10',
    },
    {
      name: 'Click Rate',
      value: campaigns?.data && campaigns.data.reduce((sum, c) => sum + c.sent_count, 0) > 0
        ? Math.round(
            (campaigns.data.reduce((sum, c) => sum + c.click_count, 0) /
              campaigns.data.reduce((sum, c) => sum + c.sent_count, 0)) *
              100
          ) + '%'
        : '0%',
      icon: ArrowTrendingUpIcon,
      color: 'text-purple-400',
      bgColor: 'bg-purple-400/10',
    },
  ];

  const getStatusBadge = (status: Campaign['status']) => {
    const classes: Record<Campaign['status'], string> = {
      draft: 'badge-warning',
      scheduled: 'badge-info',
      sending: 'badge-success animate-pulse',
      completed: 'badge-info',
      paused: 'badge-warning',
    };
    return classes[status];
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            Welcome back, {user?.name || 'there'}!
          </h1>
          <p className="mt-1 text-gray-400">Here's what's happening with your campaigns</p>
        </div>
        <Link to="/campaigns/new" className="btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          New Campaign
        </Link>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.name} className="card">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
              <div>
                <p className="text-sm text-gray-400">{stat.name}</p>
                <p className="text-2xl font-semibold text-white">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Campaigns */}
      <div className="card">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Recent Campaigns</h2>
          <Link to="/campaigns" className="text-sm text-primary-400 hover:text-primary-300">
            View all
          </Link>
        </div>

        {campaigns?.data && campaigns.data.length > 0 ? (
          <div className="space-y-4">
            {campaigns.data.map((campaign) => (
              <Link
                key={campaign.id}
                to={`/campaigns/${campaign.id}`}
                className="block p-4 bg-dark-900 rounded-lg hover:bg-dark-700 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className="text-white font-medium truncate">{campaign.name}</h3>
                      <span className={getStatusBadge(campaign.status)}>{campaign.status}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-400 truncate">{campaign.subject}</p>
                  </div>
                  <div className="ml-4 text-right">
                    <p className="text-sm font-medium text-white">{campaign.sent_count}</p>
                    <p className="text-xs text-gray-500">sent</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <MegaphoneIcon className="w-12 h-12 mx-auto text-gray-600" />
            <h3 className="mt-4 text-lg font-medium text-white">No campaigns yet</h3>
            <p className="mt-2 text-gray-400">Create your first campaign to get started</p>
            <Link to="/campaigns/new" className="btn-primary mt-4">
              <PlusIcon className="w-5 h-5 mr-2" />
              Create Campaign
            </Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-4">
        <Link
          to="/campaigns/new"
          className="card flex items-center gap-4 hover:border-primary-400/30 transition-colors"
        >
          <div className="p-3 bg-primary-400/10 rounded-lg">
            <MegaphoneIcon className="w-6 h-6 text-primary-400" />
          </div>
          <div>
            <h3 className="font-medium text-white">Create Campaign</h3>
            <p className="text-sm text-gray-400">Start a new email campaign</p>
          </div>
        </Link>

        <Link
          to="/templates"
          className="card flex items-center gap-4 hover:border-primary-400/30 transition-colors"
        >
          <div className="p-3 bg-blue-400/10 rounded-lg">
            <EnvelopeIcon className="w-6 h-6 text-blue-400" />
          </div>
          <div>
            <h3 className="font-medium text-white">Email Templates</h3>
            <p className="text-sm text-gray-400">Browse ready-to-use templates</p>
          </div>
        </Link>

        <Link
          to="/wallets"
          className="card flex items-center gap-4 hover:border-primary-400/30 transition-colors"
        >
          <div className="p-3 bg-green-400/10 rounded-lg">
            <ArrowTrendingUpIcon className="w-6 h-6 text-green-400" />
          </div>
          <div>
            <h3 className="font-medium text-white">Supported Wallets</h3>
            <p className="text-sm text-gray-400">View all crypto wallets</p>
          </div>
        </Link>
      </div>
    </div>
  );
}
